import { InputNumber, Tooltip, Divider, Button, Modal, Input } from "antd";
import { DatePicker } from "components/Utils/momentPickers";
import { QuestionCircleOutlined } from "@ant-design/icons";
import _ from "lodash";
import uuid from "react-uuid";
import moment from "moment";
import "moment/locale/pt-br";

// Services
import getApiUrl from "services/getApiUrl";
import getFieldValue from "./getFieldValue";
import { getFieldsDataByLookup, getPreviewValueFormula } from "auth/actions/objActions";

// Actions
import {
  addFormData,
  addRefFieldsFormData,
  deleteFormData,
  startLkpLoading,
  stopLkpLoading,
} from "hooks/dataOfForm";

// Utils
import checkValueIsDate from "./checkValueIsDate";
import IconTriggerModal from "../../pages/Automations/Edit/Flow/Snippets/IconTriggerModal";
import { buildIdSegment, canUseIdSegment, recordFieldKey } from "../../pages/Automations/Edit/Flow/Snippets/fieldPath";
import React from "react";
import { DateInput } from "../../pages/Automations/Edit/Flow/Actions/utils/DateInput";
import { SelectorInput } from "../../pages/Automations/Edit/Flow/Actions/utils/SelectInput";
import { RefInput } from "../../pages/Automations/Edit/Flow/Actions/utils/RefInput";
import { NumericInput } from "../../pages/Automations/Edit/Flow/Actions/utils/NumericInput";

// Função para obter as opções para campos referenciados
//
// Devolve também quantos registros a página trouxe: um registro sem valor no
// campo referenciado não vira opção, então "zero opções" não quer dizer "acabou
// o formulário". Quem pagina precisa dos dois números para saber se continua
// lendo (ver `fetchRefData`, que mantém o contrato antigo de devolver só a lista).
export const fetchRefRecords = async ({ refFields, keyWords, page, pageSize }) => {
  const records = await getApiUrl.post(
    `/objetos/formularios/campos/dados/referenciados`,
    {
      fields: refFields,
      keyword: keyWords,
      page,
      pageSize,
    }
  );
  const { message: recordsData } = records.data;
  const options = [];

  const labelCount = new Map(); // Mapa para contar rótulos repetidos

  if (recordsData) {
    recordsData.forEach((record) => {
      for (const key in record) {
        if (Object.hasOwnProperty.call(record, key)) {
          const element = record[key];
          if (key !== "_id" && key !== "createdAt") {
            let valueElement = getFieldValue(element)

            if (!_.isNumber(valueElement) && (_.isEmpty(valueElement) || _.isNull(valueElement))) {
              continue;
            }

            if (checkValueIsDate(valueElement, 'YYYY-MM-DDTHH:mm:ss[Z]')) {
              valueElement = moment(valueElement, "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY")
            }

            // A data de criação é só o desempate do rótulo. Um único registro
            // sem `createdAt` derrubava a montagem inteira das opções e a caixa
            // de seleção ficava sem nenhum registro, sem erro visível.
            const createdAtValue = record.createdAt?.[0]?.value;
            const label = createdAtValue
              ? `${valueElement} (${moment(createdAtValue, "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY HH:mm")})`
              : `${valueElement}`;
            const value = `${record._id}`;

            // Verificar se o rótulo já existe no mapa de contagem
            if (labelCount.has(label)) {
              const count = labelCount.get(label);
              labelCount.set(label, count + 1);
            } else {
              labelCount.set(label, 1);
            }

            // Adicionar o contador ao rótulo se necessário
            const finalLabel =
              labelCount.get(label) > 1
                ? `${label} (${labelCount.get(label)})`
                : label;
            // Adiciona nova opção
            options.push({
              label: finalLabel,
              value: value,
              field: key
            });
          }
        }
      }
    });
  }
  return {
    options,
    total: Array.isArray(recordsData) ? recordsData.length : 0,
  };
};

export const fetchRefData = async (params) => {
  const { options } = await fetchRefRecords(params);
  return options;
};

// Função para obter opções a partir de pesquisas em campos referenciados
const getRefData = async (params) => {
  const {
    fieldName,
    allFields,
    refFields,
    keyWords,
    page,
    pageSize,
    action,
    fromEdit,
  } = params;
  const query = {
    refFields,
    keyWords,
    page,
    pageSize,
  };

  // Tenta adicionar nome e tipo do campo ao campos
  refFields.forEach((obj) => {
    const fieldToFind = obj.field;
    // Obtém nome do campo referenciado
    const matchingField = allFields.find((item1) => item1._id === fieldToFind);
    if (matchingField) {
      obj.fieldName = matchingField.field;
      obj.fieldType = matchingField.fieldtype;
    }
  });

  // Tenta obter os dados a partir da pesquisa na caixa de seleção
  const options = await fetchRefData(query);
  const optionsFromRef = [];

  if (action === "edit") {
    if (fromEdit && fromEdit[fieldName] && fromEdit[fieldName].length > 0) {
      optionsFromRef.push({
        _id: fromEdit[fieldName].map((obj) => obj._id).join(","),
        value: fromEdit[fieldName].map((obj) => obj.value).join(", "),
      });
    }
  }

  // Organiza os dados para retornar para campo de seleção
  if (Array.isArray(options) && options.length > 0) {
    // Remove opções vazias " (DD/MM/YYYY HH:mm:ss)"
    const nonEmptyCreatedAtOpts = options.filter(
      (item) => !item.label.startsWith(" (")
    );
    if (optionsFromRef && optionsFromRef.length > 0) {
      return _.concat(
        optionsFromRef.map((opt) => ({
          label: opt.value,
          value: opt._id,
        })),
        nonEmptyCreatedAtOpts.map((opt) => ({
          label: opt.label,
          value: `${opt.value}_rng@record_${uuid()}`,
        }))
      );
    } else {
      return nonEmptyCreatedAtOpts.map((opt) => ({
        label: opt.label,
        value: `${opt.value}_rng@record_${uuid()}`,
      }));
    }
  } else {
    return [];
  }
};

// Função para obter registros a partir de pesquisas em campos de lookup
export const getLookupData = async (params) => {
  const { tableRef, tableAction, selectedField, selectedOpts, dispatch } =
    params;
  if (selectedOpts) {
    // Sinaliza o início do carregamento dos campos de retorno (exibe o loading)
    if (dispatch) dispatch(startLkpLoading());
    try {
      // Remove rng das opções selecionadas
      // TODO: trabalhar com ${uuid},${uuid}
      const cleanedFromRng = selectedOpts.substring(
        0,
        selectedOpts.indexOf("_rng@record_")
      );

      // Busca cada campo de retorno de forma independente. Um campo que
      // falhe ou demore (timeout) não impede o preenchimento dos demais nem
      // trava o loading — cada requisição é resolvida isoladamente.
      await Promise.all(
        selectedField.checkValueIn.map(async (childObj) => {
          try {
            const options = await getFieldsDataByLookup({
              table: selectedField?.getValueOf[0].form.replace("obj_", ""),
              id: cleanedFromRng,
              fieldId: childObj.field,
              checkIn: [childObj],
            });

            if (
              options &&
              options.success &&
              options.message &&
              options.message.length > 0
            ) {
              const childFieldName = options.message[0].fieldName;
              const childRecordId = options.message[0].id;
              let childFieldValue = options.message[0].value;

              // Verifica se o valor retornado é do tipo data
              if (
                childFieldValue &&
                checkValueIsDate(childFieldValue, "YYYY-MM-DDTHH:mm:ss[Z]")
              ) {
                childFieldValue = moment(
                  childFieldValue,
                  "YYYY-MM-DDTHH:mm:ss[Z]"
                ).format("MM/DD/YYYY");
              }

              // Verifica se o valor retornado é do tipo numérico
              if (childFieldValue && _.isNumber(childFieldValue)) {
                childFieldValue = childFieldValue.toString();
              }

              if (tableAction === undefined && childFieldName && childFieldValue) {
                // Atualiza os campos com os valores
                tableRef.current.setFieldsValue({
                  [`${childFieldName}`]: childFieldValue,
                });
              }

              dispatch(
                addFormData(childFieldName, {
                  _id: childRecordId,
                  value: childFieldValue,
                })
              );
            }
          } catch (error) {
            // Não interrompe o carregamento dos outros campos de retorno
            // eslint-disable-next-line no-console
            console.error(
              `[LKP] Falha ao carregar o campo de retorno ${childObj.field}:`,
              error
            );
          }
        })
      );
    } finally {
      // Sinaliza o fim do carregamento dos campos de retorno (oculta o loading)
      if (dispatch) dispatch(stopLkpLoading());
    }
  }
};

const createColumnTitle = (title, tooltip) => (
  <>
    {title}
    <Tooltip placement="top" title={tooltip}>
      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
    </Tooltip>
  </>
);

const columnConfigurations = {
  _id: {
    title: createColumnTitle("ID", "Identificação do registro"),
    dataIndex: "_id",
    key: "_id",
    copyable: true,
    hideInSearch: false,
    hideInForm: true,
    fieldProps: {
      placeholder: "",
    },
    sorter: false,
  },
  createdAt: {
    title: createColumnTitle("Criado em", "Data do registro salvo"),
    dataIndex: "createdAt",
    key: "createdAt",
    hideInForm: true,
    valueType: "date",
    renderText: (text) => {
      if (text) {
        return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").valueOf();
      }
    },
    fieldProps(form, config) {
      return {
        ...config,
        format: "DD/MM/YYYY HH:mm",
        placeholder: "",
      };
    },
    sorter: (a, b) =>
      moment(a.createdAt, "YYYY-MM-DDTHH:mm:ss[Z]").unix() -
      moment(b.createdAt, "YYYY-MM-DDTHH:mm:ss[Z]").unix(),
  },
  updatedAt: {
    title: createColumnTitle("Atualizado em", "Data do registro atualizado"),
    dataIndex: "updatedAt",
    key: "updatedAt",
    hideInSearch: true,
    hideInForm: true,
    valueType: "date",
    renderText: (text) => {
      if (text) {
        return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").valueOf();
      }
    },
    fieldProps(form, config) {
      return {
        ...config,
        format: "DD/MM/YYYY HH:mm",
        placeholder: "",
      };
    },
    sorter: (a, b) => {
      const timeA =
        a.updatedAt && moment(a.updatedAt, "YYYY-MM-DDTHH:mm:ss[Z]").isValid()
          ? moment(a.updatedAt, "YYYY-MM-DDTHH:mm:ss[Z]").unix()
          : -Infinity;
      const timeB =
        b.updatedAt && moment(b.updatedAt, "YYYY-MM-DDTHH:mm:ss[Z]").isValid()
          ? moment(b.updatedAt, "YYYY-MM-DDTHH:mm:ss[Z]").unix()
          : -Infinity;
      return timeA - timeB;
    },
  },
};

const clearForm = (fields, selectedField, selectedValue, ref, dp, action) => {
  const selectedFieldIds = selectedField.checkValueIn.map(
    (field) => field.field
  );
  const clearFields = fields.filter((item) =>
    selectedFieldIds.includes(item._id)
  );
  clearFields.forEach((obj) => {
    // Verifica se está editando o registro
    if (action === "edit" || action === "multipleEdit") {
      // Verifica se campo está vazio
      if (selectedValue === undefined) {
        // Possibilita gerar campos vazio em mountRecords()
        ref.current.setFieldsValue({
          [selectedField.field]: "", // campo pai
        });
      }
      // Verifica se o campo é do tipo data ou tempo
      if (obj.fieldtype === "Date" || obj.fieldtype === "Time") {
        ref.current.setFieldsValue({
          [obj.field]: moment(),
        });
      } else {
        ref.current.setFieldsValue({
          [obj.field]: "",
        });
      }
    } else {
      ref.current.setFieldsValue({
        [obj.field]: null,
      });
    }
  });
  dp(deleteFormData(_.map(clearFields, "field")));
};

const refLkpOnChange = async (
  allFields,
  field,
  currentValue,
  nativeCurrentValue,
  ref,
  dp,
  action
) => {
  dp(
    addRefFieldsFormData(
      field.field,
      _.compact(
        _.map(nativeCurrentValue ? [nativeCurrentValue] : [], (obj) =>
          _.isEmpty(obj) ? null : obj
        )
      )
    )
  );
  // Limpa os campos de retorno no formulário
  clearForm(allFields, field, currentValue, ref, dp, action);
  // Obtém os dados do campo de retorno e preenche o formulário
  await getLookupData({
    tableRef: ref,
    selectedField: field,
    selectedOpts: currentValue,
    dispatch: dp,
  });
};

export default function buildColumns(columns, fields, refFields, sorter = false, withFieldIdRef = false) {
  if (columns && _.size(columns) > 0 && fields && _.size(fields) > 0) {

    return ["_id", ...columns, "createdAt", "updatedAt"].flatMap((x) => {

      const selectedField = fields.find((y) => y.field === x);

      let extraColumn = null;
      if (selectedField) {
        const { _id: fieldId, fieldtype: fieldType } = selectedField;
        if (fieldType === "refLookup") {
          if (withFieldIdRef) {
            extraColumn = ({
              title: `${x} (ID)`,
              dataIndex: `${x} (ID)`,
              key: `${x} (ID)`,
              valueType: "Text",
              hideInSearch: true,
              fieldProps(form, config) {
                return {
                  ...config,
                  placeholder: "",
                };
              },
              placeholder: "",
              sorter: false,
            });
          }
        } else {
          const father = fields.find(
            (h) =>
              h.fieldtype === "refLookup" &&
              h.checkValueIn.find((z) => z.field === fieldId)
          );
          selectedField.isLookup = !!father;

        }

        if (selectedField.checkValueIn || selectedField.getValueOf) {
          const allRefs = [...(selectedField.checkValueIn ?? []), ...(selectedField.getValueOf ?? [])];
          selectedField.father = allRefs.map(value => {
            const refField = refFields.find(ref => ref._id === value.field)
            if (refField) {
              return refField;
            } else {
              return null
            }
          }).filter(item => item)
        }

      }

      if (columnConfigurations[x]) {
        const column = { ...columnConfigurations[x] };
        if (sorter && x !== "_id") {
          column.sorter = sorter;
        }
        if (selectedField) {
          column.hideInSearch = selectedField.isLookup;
        }
        return column;
      }
      // Verifica qual tipo do campo e cria a coluna
      if (
        selectedField &&
        selectedField.fieldtype === "Text" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          hideInSearch: selectedField.isLookup,
          fieldProps: {
            placeholder: "",
          },
          renderText: (text) => {
            if (text) {
              if (_.isEmpty(selectedField.sufixo)) {
                return text;
              } else {
                return `${text} ${selectedField.sufixo}`;
              }
            }
          },
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "Numeric" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          hideInSearch: selectedField.isLookup,
          valueType: "number",
          fieldProps: {
            placeholder: "",
          },
          renderText: (text) => {
            if (text) {
              if (_.isEmpty(selectedField.sufixo)) {
                return text;
              } else {
                return `${text} ${selectedField.sufixo}`;
              }
            }
          },
          formItemRender: () => (
            <InputNumber formatter={null} keyboard={true} />
          ),
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "Date" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: 'text',
          hideInSearch: selectedField.isLookup,
          formItemRender: (props) => {
            return (
              <DatePicker.RangePicker
                {...props}
                placeholder={""}
                format="DD/MM/YYYY"
                allowClear
                style={{ width: '100%' }}
                ranges={{
                  Hoje: [moment().startOf('day'), moment().endOf('day')],
                  'Este mês': [
                    moment().startOf('month'),
                    moment().endOf('month'),
                  ],
                }}
              />
            );
          },
          renderText: (text) => {
            if (text) {
              if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY");
              } else {
                return '-';
              }
            }
            return '-';
          },
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "Time" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "time",
          hideInSearch: selectedField.isLookup,
          renderText: (text) => {
            if (text) {
              return moment(text, "HH:mm").valueOf();
            }
          },
          fieldProps(form, config) {
            return {
              ...config,
              format: "HH:mm",
              placeholder: "",
            };
          },
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "uniqueSelection" &&
        !selectedField.isLookup
      ) {

        const options = Object.keys(selectedField.checkBox).map((key) => ({
          label: selectedField.checkBox[key],
          value: selectedField.checkBox[key],
        }));

        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "select",
          hideInSearch: selectedField.isLookup,
          renderText: (opt) => {
            if (opt) {
              const text = (_.isArray(opt) ? opt : _.isString(opt) ? opt.split(",") : []);
              if (text) {
                return <span>{text}</span>
              }
              return "";
            } else {
              return "";
            }
          },
          fieldProps: {
            mode: "tags",
            placeholder: "",
            options
          },
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "multipleSelection" &&
        !selectedField.isLookup
      ) {
        const options = Object.keys(selectedField.checkBox).map((key) => ({
          label: selectedField.checkBox[key],
          value: selectedField.checkBox[key],
        }));

        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "select",
          renderText: (opt) => {
            if (opt) {
              return (_.isArray(opt) ? opt : _.isString(opt) ? opt.split(",") : []).join(",")
            } else {
              return "";
            }
          },
          fieldProps: {
            mode: "tags",
            placeholder: "",
            options
          },
          hideInSearch: selectedField.isLookup,
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "label"
      ) {
        return null;
      }
      if (
        selectedField &&
        selectedField.fieldtype === "refUniqueSelection" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "text",
          fieldProps: {
            placeholder: "",
          },
          renderText: (text) => {
            if (text) {
              if (moment(text, 'YYYY-MM-DDTHH:mm:ss[Z]', true).isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
              } else if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
              }
              return text;
            }
          },
          hideInSearch: selectedField.isLookup,
          sorter,
        };
      }
      if (
        selectedField &&
        selectedField.fieldtype === "refMultipleSelection" &&
        !selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "text",
          fieldProps: {
            placeholder: "",
          },
          renderText: (text) => {
            if (text) {
              if (moment(text, 'YYYY-MM-DDTHH:mm:ss[Z]', true).isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
              }
              return text;
            }
          },
          hideInSearch: selectedField.isLookup,
          sorter,
        };
      }


      if (
        selectedField &&
        selectedField.fieldtype === "refLookup" &&
        !selectedField.isLookup &&
        selectedField.father && selectedField.father.length > 0 && selectedField.father[0].fieldtype === "multipleSelection"
      ) {
        const itemToReturn = {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "select",
          fieldProps(form, config) {
            return {
              ...config,
              placeholder: "",
              mode: "tags"
            };
          },
          // hideInSearch: true,
          placeholder: "",
          renderText: (text) => {
            if (text) {
              if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
              }
              return text;
            }
          },
          sorter,
        };
        return (extraColumn) ? [itemToReturn, extraColumn] : itemToReturn;
      }

      if (
        selectedField &&
        selectedField.fieldtype === "refLookup" &&
        !selectedField.isLookup &&
        selectedField.father && selectedField.father.length > 0 && selectedField.father[0].fieldtype === "Date"
      ) {
        const itemToReturn = {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "text",
          hideInSearch: selectedField.isLookup,
          transform: (value) => {
            const item = [...value];
            item['fatherFieldType'] = selectedField.father[0].fieldtype;
            return { [`${x}`]: item };
          },
          formItemRender: (props) => {
            return (
              <DatePicker.RangePicker
                {...props}
                placeholder={""}
                format="DD/MM/YYYY"
                allowClear
                style={{ width: '100%' }}
                ranges={{
                  Hoje: [moment().startOf('day'), moment().endOf('day')],
                  'Este mês': [
                    moment().startOf('month'),
                    moment().endOf('month'),
                  ],
                }}
              />
            );
          },
          renderText: (text) => {
            if (text) {
              if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY");
              } else {
                return '-';
              }
            }
            return '-';
          },
          // hideInSearch: true,
          placeholder: "",
          sorter,
        };
        return (extraColumn) ? [itemToReturn, extraColumn] : itemToReturn;
      }

      if (
        selectedField &&
        selectedField.fieldtype === "refLookup" &&
        !selectedField.isLookup &&
        selectedField.father && selectedField.father.length > 0 && selectedField.father[0].fieldtype === "Time"
      ) {
        const itemToReturn = {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "time",
          hideInSearch: selectedField.isLookup,
          renderText: (text) => {
            if (text) {
              return moment(text, "HH:mm").valueOf();
            }
          },
          fieldProps(form, config) {
            return {
              ...config,
              format: "HH:mm",
              placeholder: "",
            };
          },
          // hideInSearch: true,
          placeholder: "",
          sorter,
        };
        return (extraColumn) ? [itemToReturn, extraColumn] : itemToReturn;
      }

      if (
        selectedField &&
        selectedField.fieldtype === "refLookup" &&
        !selectedField.isLookup
      ) {
        const itemToReturn = {
          title: x,
          dataIndex: x,
          key: x,
          fieldProps(form, config) {
            return {
              ...config,
              placeholder: "",
            };
          },
          // hideInSearch: true,
          placeholder: "",
          renderText: (text) => {
            if (text) {
              if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
              }
              return text;
            }
          },
          sorter,
        };
        return (extraColumn) ? [itemToReturn, extraColumn] : itemToReturn;
      }

      if (
        selectedField &&
        selectedField.fieldtype === "Date" &&
        selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "text",
          formItemRender: (props) => {
            return (
              <DatePicker.RangePicker
                {...props}
                placeholder={""}
                format="DD/MM/YYYY"
                allowClear
                style={{ width: '100%' }}
                ranges={{
                  Hoje: [moment().startOf('day'), moment().endOf('day')],
                  'Este mês': [
                    moment().startOf('month'),
                    moment().endOf('month'),
                  ],
                }}
              />
            );
          },
          renderText: (text) => {
            if (text) {
              if (moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").isValid()) {
                return moment(text, "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY");
              } else {
                return '-';
              }
            }
            return '-';
          },
          // hideInSearch: selectedField.isLookup,
          sorter,
        };
      }


      if (
        selectedField &&
        selectedField.fieldtype === "multipleSelection" &&
        selectedField.isLookup
      ) {

        return {
          valueType: "select",
          title: x,
          dataIndex: x,
          key: x,
          fieldProps(form, config) {
            return {
              ...config,
              placeholder: "",
              mode: "tags",
              options: Object.entries(selectedField.checkBox).map(([key, value]) => ({ value: value, label: value }))
            };
          },
          // hideInSearch: selectedField.isLookup,
          placeholder: "",
          sorter,
        };
      }

      if (
        selectedField &&
        selectedField.fieldtype === "uniqueSelection" &&
        selectedField.isLookup
      ) {
        return {
          valueType: "select",
          title: x,
          dataIndex: x,
          key: x,
          fieldProps(form, config) {
            return {
              ...config,
              placeholder: "",
              mode: "tags",
              options: Object.entries(selectedField.checkBox).map(([key, value]) => ({ value: value, label: value }))
            };
          },
          // hideInSearch: selectedField.isLookup,
          placeholder: "",
          sorter,
        };
      }

      if (
        selectedField &&
        selectedField.fieldtype === "Time" &&
        selectedField.isLookup
      ) {
        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: "time",
          // hideInSearch: selectedField.isLookup,
          renderText: (text) => {
            if (text) {
              return moment(text, "HH:mm").valueOf();
            }
          },
          fieldProps(form, config) {
            return {
              ...config,
              format: "HH:mm",
              placeholder: "",
            };
          },
          sorter,
        };
      }

      if (
        selectedField &&
        selectedField.fieldtype === "formula"
      ) {
        const renderText = selectedField.returntype !== 'Numeric'
          ? _.toString
          : (text) => {
            const number = _.toNumber(text);
            return isNaN(number) ? 0 : number;
          };

        return {
          title: x,
          dataIndex: x,
          key: x,
          valueType: selectedField.returntype !== 'Numeric' ? "text" : 'digit',
          fieldProps: {
            placeholder: "",
          },
          renderText,
          hideInSearch: selectedField.isLookup,
          sorter,
        };
      }

      return {
        title: x,
        dataIndex: x,
        key: x,
        fieldProps(form, config) {
          return {
            ...config,
            placeholder: "",
          };
        },
        renderText: (text) => {
          if (moment(text, ["YYYY-MM-DDTHH:mm:ss[Z]", "YYYY-MM-DDTHH:mm:ss.sss[Z]"], true).isValid()) {
            return moment(text, ["YYYY-MM-DDTHH:mm:ss[Z]", "YYYY-MM-DDTHH:mm:ss.sss[Z]"], true).format("DD/MM/YYYY HH:mm")
          }
          return text;
        },
        // hideInSearch: selectedField.isLookup,
        placeholder: "",
        sorter,
      };
    }).filter(item => item);
  } else {
    return [];
  }
}

const refFieldTypesForPreview = ["refLookup", "refUniqueSelection", "refMultipleSelection"];

// Resolve o valor interno do select de campos ref ("<idRegistro>[,...]_rng@record_<uuid>")
// para o mesmo formato gravado ao salvar um registro: [{ _id: idRegistro, value: valorExibido }].
// Sem isso, a fórmula recebe o id interno no lugar do valor real do campo referenciado.
async function resolveRefFieldForPreview(refField, rawValue) {
  const refInfo = refField.getValueOf?.[0];
  const ids = String(rawValue)
    .replace(/_rng@record_.*/, "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  if (!refInfo || ids.length === 0) return null;

  const resolved = await Promise.all(
    ids.map(async (id) => {
      const response = await getFieldsDataByLookup({
        table: refInfo.form.replace("obj_", ""),
        id,
        fieldId: refInfo.field,
        checkIn: [refInfo],
      });

      const value = response?.success ? response?.message?.[0]?.value : null;
      return {
        _id: id,
        value: value ?? "",
      };
    })
  );

  return resolved;
}

async function getInfoToPreviewFormula(fields, formRef, fromEdit, currentForm, selectedField, idRecord = null) {
  const values = formRef.current?.getFieldsValue();
  const records = {};

  for (const f of fields) {
    let value = values?.[f.field];
    let id = f._id;
    let refEntries = null;

    // Campos referenciados não usam o atalho do fromEdit: o _id armazenado na edição
    // carrega o sufixo "_rng@record_<uuid>" e precisa passar pela resolução abaixo,
    // senão o backend recebe o id sujo e a fórmula avalia como 0.
    if (fromEdit?.[f.field] && !refFieldTypesForPreview.includes(f.fieldtype)) {
      const match = fromEdit[f.field].find(item => item._id === value);

      if (match) {
        id = match._id;
        value = match.value;
      }
    }

    if (refFieldTypesForPreview.includes(f.fieldtype) && value) {
      const rawValues = Array.isArray(value) ? value : [value];
      const unresolved = rawValues.filter(
        (item) => typeof item === "string" && item.includes("_rng@record_")
      );

      if (unresolved.length > 0) {
        try {
          const resolvedGroups = await Promise.all(
            unresolved.map((item) => resolveRefFieldForPreview(f, item))
          );
          const entries = resolvedGroups.filter(Boolean).flat();
          if (entries.length > 0) {
            refEntries = entries;
          }
        } catch (error) {
          // Falha na resolução cai no fallback abaixo (value vazio)
        }

        // Evita enviar o id interno do select como valor caso a resolução falhe
        if (!refEntries) {
          refEntries = [{ _id: String(rawValues[0]).replace(/_rng@record_.*/, ""), value: "" }];
        }
      }
    }

    if (moment.isMoment(value)) {
      value = value.toISOString();
    }

    // Campos de seleção guardam [{_id, value}] no formRef; envia cada entrada com o
    // valor plano, senão a fórmula recebe "[object Object]" e as comparações falham.
    if (!refEntries && Array.isArray(value)) {
      const entries = value
        .filter((item) => item !== null && item !== undefined)
        .map((item) =>
          typeof item === "object" && !moment.isMoment(item) && "value" in item
            ? { _id: item._id ?? id, value: item.value ?? "" }
            : { _id: id, value: moment.isMoment(item) ? item.toISOString() : item }
        );
      refEntries = entries.length > 0 ? entries : [{ _id: id, value: "" }];
    }

    records[f.field] = refEntries ?? [
      {
        _id: id,
        value: value ?? ""
      }
    ];
  }

  const response = await getPreviewValueFormula({
    formName: currentForm,
    idFieldFormula: selectedField._id,
    idRecord: idRecord,
    typePreview: "FAKE_RECORD",
    fakeFields: [records]
  });

  if (!response.success) {
    Modal.error({
      title: "Erro ao calcular fórmula",
      content: response.message ?? "Erro ao calcular fórmula."
    });
    return;
  }

  const responseValue = Object.values(response.message)[0]?.value;
  const formulaResult =
    responseValue == null ||
    responseValue === "" ||
    (Array.isArray(responseValue) && responseValue.length === 0)
    ? "Sem informação."
    : responseValue;

  formRef.current?.setFieldsValue({
    [selectedField.field]: formulaResult
  });
}

export function buildSchemaColumns(
  columns,
  fields,
  refFields,
  fromEdit,
  formRef,
  action,
  isImport,
  dispatch,
  currentForm = '',
  idRecord = null
) {
  const onlyFormFields =
    action === "new" || isImport == true
      ? _.map(
        fields.filter((obj) => obj.isDeleted !== true),
        "field"
      )
      : columns.filter((obj) => obj !== "_id" || "createdAt" || "updatedAt");
  if (columns && _.size(columns) > 0 && fields && _.size(fields) > 0) {
    return onlyFormFields
      .sort((a, b) => a.index - b.index)
      .map((x) => {
        const selectedField = fields.find((y) => y.field === x);
        if (selectedField) {
          if (selectedField.fieldtype === "Text") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              initialValue: "",
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    whitespace: !!selectedField.requiredField,
                    message: `${x} é obrigatório.`,
                  },
                ],
              },
              fieldProps: {
                style: {
                  width: "100%",
                },
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
                suffix: selectedField.sufixo ? selectedField.sufixo : null,
                allowClear: _.isEmpty(selectedField.sufixo) ? true : false,
              },
            };
          }
          else if (selectedField.fieldtype === "Numeric") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "digit",
              initialValue: "",
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
              fieldProps: {
                style: {
                  width: "100%",
                },
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
              },
              formItemRender: (_, { fieldProps }) => (
                <InputNumber
                  {...fieldProps}
                  formatter={null}
                  keyboard={true}
                  prefix={selectedField.sufixo ? selectedField.sufixo : null}
                />
              ),
            };
          }
          else if (selectedField.fieldtype === "Date") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "date",
              initialValue: selectedField.isReadOnly === undefined ? !!selectedField.requiredField ? moment() : null : null,
              fieldProps(form, config) {
                return {
                  ...config,
                  format: "DD/MM/YYYY",
                  disabled: selectedField.isReadOnly,
                  placeholder: selectedField.isReadOnly ?? "",
                  style: {
                    width: "100%",
                  },
                };
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              }
            };
          }
          else if (selectedField.fieldtype === "Time") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "time",
              initialValue: selectedField.isReadOnly === undefined ? !!selectedField.requiredField ? moment() : null : null,
              fieldProps(form, config) {
                return {
                  ...config,
                  format: "HH:mm",
                  disabled: selectedField.isReadOnly,
                  placeholder: selectedField.isReadOnly ?? "",
                  style: {
                    width: "100%",
                  },
                };
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
              colProps: {
                span: 24,
              },
            };
          }
          else if (selectedField.fieldtype === "uniqueSelection") {
            let opts = [];
            Object.keys(selectedField.checkBox).reduce((enumObj, key) => {
              opts.push({
                label: selectedField.checkBox[key],
                value: selectedField.checkBox[key],
              });
            }, {});
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "select",
              // valueEnum,
              fieldProps: {
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
                options: opts,
              },
              colProps: {
                span: 24,
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
            };
          }
          else if (selectedField.fieldtype === "multipleSelection") {
            let opts = [];
            Object.keys(selectedField.checkBox).reduce((enumObj, key) => {
              opts.push({
                label: selectedField.checkBox[key],
                value: selectedField.checkBox[key],
              });
            }, {});
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "select",
              fieldProps: {
                mode: "multiple",
                options: opts,
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
              colProps: {
                span: 24,
              },
            };
          }
          else if (selectedField.fieldtype === "refUniqueSelection") {
            const uniqueRefFields = _.filter(refFields, (item) =>
              _.map(selectedField.refFields, "field").includes(item._id)
            ).map((obj) => {
              return ({
                form: obj._index,
                fieldName: obj.field,
                fieldType: obj.fieldtype
              });
            });
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "select",
              request: selectedField.isReadOnly ? {} : getRefData,
              params: {
                allFields: fields,
                fieldName: selectedField.field,
                refFields: uniqueRefFields || [],
                page: 1,
                pageSize: 1000,
                fromEdit,
                action,
              },
              fieldProps: {
                mode: "single",
                showSearch: true,
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
                onChange: async (current, nativeCurrent) => {
                  dispatch(
                    addRefFieldsFormData(
                      selectedField.field,
                      _.compact(
                        _.map(nativeCurrent ? [nativeCurrent] : [], (obj) =>
                          _.isEmpty(obj) ? null : obj
                        )
                      )
                    )
                  );
                },
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
            };
          }
          else if (selectedField.fieldtype === "refMultipleSelection") {
            const multipleRefFields = _.filter(refFields, (item) =>
              _.map(selectedField.refFields, "field").includes(item._id)
            ).map((obj) => ({
              form: obj._index,
              fieldName: obj.field,
              fieldType: obj.fieldtype
            }));
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "select",
              request: selectedField.isReadOnly ? {} : getRefData,
              params: {
                allFields: fields,
                fieldName: selectedField.field,
                refFields: multipleRefFields,
                page: 1,
                pageSize: 1000,
                fromEdit,
                action,
              },
              fieldProps: {
                mode: "multiple",
                showSearch: true,
                disabled: selectedField.isReadOnly,
                placeholder: selectedField.isReadOnly ?? "",
                onChange: async (current, nativeCurrent) => {
                  dispatch(
                    addRefFieldsFormData(
                      selectedField.field,
                      _.compact(
                        _.map(nativeCurrent, (obj) =>
                          _.isEmpty(obj) ? null : obj
                        )
                      )
                    )
                  );
                }
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
            };
          }
          else if (selectedField.fieldtype === "refLookup") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "select",
              request: getRefData,
              params: {
                allFields: fields,
                fieldName: selectedField.field,
                refFields: selectedField.getValueOf,
                page: 1,
                pageSize: 1000,
                fromEdit,
                action,
              },
              fieldProps: {
                showSearch: true,
                placeholder: "",
                onChange: async (current, nativeCurrent) =>
                  refLkpOnChange(
                    fields,
                    selectedField,
                    current,
                    nativeCurrent,
                    formRef,
                    dispatch,
                    action
                  ),
                onClear: () => {
                  //clearForm(fields, selectedField, formRef, dispatch, action);
                },
              },
              formItemProps: {
                required: !!selectedField.requiredField,
                rules: [
                  {
                    required: false,
                  }
                ]
              },
            };
          }
          else if (selectedField.fieldtype === "formula") {
            return {
              title: x,
              dataIndex: x,
              key: x,
              valueType: "text",
              formItemRender: (_, { fieldProps }) => (
                <Input
                  {...fieldProps}
                  disabled
                  addonAfter={
                    <Button
                      disabled={selectedField.isReadOnly}
                      size="small"
                      onClick={() => getInfoToPreviewFormula(
                        fields, formRef, fromEdit, currentForm, selectedField, idRecord
                      )}
                    >
                      Pré-visualizar
                    </Button>
                  }
                />
              ),
              fieldProps: {
                showSearch: true,
                placeholder: selectedField.isReadOnly ? "" : "Campo calculado",
                disabled: true
              }
            };
          }
          else if (selectedField.fieldtype === "label") {
            return {
              dataIndex: x,
              key: x,
              valueType: "text",
              fieldProps: {
                showSearch: true,
                placeholder: x,
                disabled: true
              },
              formItemRender: () => (
                <Divider orientation="center" plain>
                  {x}
                </Divider>
              )
            };
          }
        }
        // Se campo não for encontrado
        return {
          title: x,
          dataIndex: x,
          key: x,
          colProps: {
            span: 24,
          },
          fieldProps: {
            disabled: selectedField.isReadOnly,
            placeholder: selectedField.isReadOnly ?? "",
          },
        };
      })
      .filter(item => item)
      // Exibe a dica configurada no campo (Configurações de Formulários)
      // como tooltip ao lado do label e como placeholder na criação/edição
      // de registros
      .map((column) => {
        const selectedField = fields.find((y) => y.field === column.dataIndex);
        if (column.title && !_.isEmpty(selectedField?.dica)) {
          const decorated = { ...column, tooltip: selectedField.dica };
          // fieldProps pode ser objeto ou função (ex.: Date/Time)
          if (typeof column.fieldProps === "function") {
            decorated.fieldProps = (form, config) => ({
              ...column.fieldProps(form, config),
              placeholder: selectedField.dica,
            });
          } else {
            decorated.fieldProps = {
              ...column.fieldProps,
              placeholder: selectedField.dica,
            };
          }
          return decorated;
        }
        return column;
      });
  } else {
    return [];
  }
}

export function buildSchemaColumnsAutomation(form, columns = [], fieldName) {

  return columns
    .map((selectedField, index) => {
      const x = selectedField.field;
      const { fieldtype, requiredField, sufixo, checkBox, refFields, field, _valueIndex, _valueId } = selectedField;
      // O caminho leva os editores (raio, data, seletores) até o valor dentro de
      // action.config. Endereçamos pelo `id` do campo, que é estável: a ordem da
      // lista de valores é a de inclusão e não a de exibição, então qualquer
      // endereçamento posicional passa a apontar para o campo errado assim que um
      // campo é adicionado ou removido. O índice fica como último recurso.
      const valuePath = canUseIdSegment(_valueId)
        ? `${fieldName}.${buildIdSegment(_valueId)}.value`
        : `${fieldName}.${_valueIndex ?? index}.value`;
      // O rótulo continua sendo o nome do campo; a chave do formulário é o `id`,
      // que não muda quando o campo é renomeado no formulário de origem (ver
      // recordFieldKey em fieldPath.js).
      const columnKey = recordFieldKey(_valueId ?? selectedField._id, x);
      const commonProps = {
        title: x,
        dataIndex: columnKey,
        key: columnKey,
        fieldProps: {
          placeholder: "",
          style: { width: "100%" },
        },
      };

      switch (fieldtype) {
        case "Text":
          return {
            ...commonProps,
            initialValue: "",
            fieldProps: {
              ...commonProps.fieldProps,
              suffix: sufixo ?? null,
              allowClear: _.isEmpty(sufixo),
              addonAfter: <IconTriggerModal fieldName={valuePath} fieldLabel={x} />
            },
          };
        case "Numeric":
          // Sem `valueType: "digit"`: o InputNumber que ele traz descarta em
          // silêncio tudo que não parseia como número — inclusive as anotações
          // gravadas pelo raio. O NumericInput valida por conta própria e mantém
          // o conteúdo recusado na tela com o motivo.
          return {
            ...commonProps,
            initialValue: "",
            formItemRender: () => (
              <NumericInput prefix={sufixo ? sufixo : null} fieldName={valuePath} fieldLabel={x} />
            ),
          };
        case "Date":
          return {
            ...commonProps,
            fieldProps: {
              ...commonProps.fieldProps,
            },
            formItemRender: () => <DateInput type={'date'} fieldName={valuePath} fieldLabel={x} />,
          };
        case "Time":
          return {
            ...commonProps,
            fieldProps: {
              ...commonProps.fieldProps,
            },
            formItemRender: () => <DateInput type={'time'} fieldName={valuePath} fieldLabel={x} />,
          };
        case "multipleSelection":
        case "uniqueSelection":
          const options = Object.keys(checkBox).map(key => ({
            label: checkBox[key],
            value: checkBox[key],
          }));
          return {
            ...commonProps,
            fieldProps: {
              ...commonProps.fieldProps,
            },
            formItemRender: () => <SelectorInput options={options} mode={fieldtype} fieldName={valuePath} fieldLabel={x} />,
          };
        case "refUniqueSelection":
        case "refMultipleSelection":
        case "refLookup":
          return {
            ...commonProps,
            fieldProps: {
              ...commonProps.fieldProps,
            },
            formItemRender: () => <RefInput field={selectedField} mode={fieldtype} fieldName={valuePath} fieldLabel={x} />,
          };
        default:
          return {
            ...commonProps,
          };
      }
    });
}


